import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useMutation } from '@tanstack/react-query';
import { authAPI } from '../lib/api';
import { GiftIcon } from '@heroicons/react/24/outline';
import {
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

export default function Register() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    affiliateCode: '',
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Auto-fill affiliate code from URL (?ref=CODE) or cookie
  useEffect(() => {
    const refFromUrl = searchParams.get('ref');
    if (refFromUrl) {
      setFormData(prev => ({ ...prev, affiliateCode: refFromUrl.trim().toUpperCase() }));
      return;
    }
    // Check cookie
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'gbr_ref' && value) {
        setFormData(prev => ({ ...prev, affiliateCode: value.trim().toUpperCase() }));
        break;
      }
    }
  }, [searchParams]);

  const { mutate, isPending } = useMutation({
    mutationFn: authAPI.register,
    onSuccess: (data) => {
      setErrorMessage(null);
      // Show verification needed message instead of auto-login
      if (data.requiresVerification) {
        setRegistrationSuccess(true);
      }
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || 'Registration failed');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }
    if (!acceptTerms) {
      setErrorMessage('Please accept the Terms of Service');
      return;
    }
    mutate({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      affiliateCodeUsed: formData.affiliateCode || undefined,
    });
  };

  // Show success screen after registration
  if (registrationSuccess) {
    return (
      <>
        <Helmet>
          <title>Check your Email | GetBookReviews</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="card-dark text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <EnvelopeIcon className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-2xl font-serif font-bold text-white mb-4">
                Check your inbox! 📬
              </h1>
              <p className="text-gray-400 mb-6">
                We've sent a verification link to <strong className="text-white">{formData.email}</strong>. 
                Please click the link to activate your account.
              </p>
              <div className="p-4 rounded-xl bg-primary-500/10 border border-primary-500/30 mb-6">
                <p className="text-sm text-gray-400">
                  Didn't receive the email? Check your spam folder or{' '}
                  <Link to="/resend-verification" className="text-primary-400 hover:underline">
                    resend verification email
                  </Link>
                </p>
              </div>
              <Link to="/login" className="btn-secondary w-full block">
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  const passwordsMatch = formData.password === formData.confirmPassword && formData.password.length > 0;
  const passwordStrong = formData.password.length >= 8;

  return (
    <>
      <Helmet>
        <title>Register | GetBookReviews</title>
        <meta name="description" content="Create your free account at GetBookReviews and start getting real reviews for your books." />
      </Helmet>

      <div className="min-h-screen bg-dark-950 bg-book-pattern flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3">
              <img src="/brand/logo-book-96.png" alt="" width={48} height={48} className="w-12 h-12" />
              <span className="text-2xl font-serif font-bold gradient-text">
                GetBookReviews
              </span>
            </Link>
          </div>

          {/* Card */}
          <div className="card-dark">
            <h1 className="text-2xl font-serif font-bold text-white text-center mb-2">
              Create Your Account
            </h1>
            <p className="text-gray-400 text-center mb-8">
              Register free and get started
            </p>

            {errorMessage && (
              <div className="flex items-center gap-2 p-4 mb-6 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
                <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <UserIcon className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    required
                    minLength={2}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-dark pl-11"
                    placeholder="John Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <EnvelopeIcon className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-dark pl-11"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <LockClosedIcon className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-dark pl-11"
                    placeholder="At least 8 characters"
                  />
                </div>
                {formData.password && (
                  <div className={`flex items-center gap-1 mt-1 text-xs ${passwordStrong ? 'text-green-400' : 'text-yellow-400'}`}>
                    {passwordStrong ? (
                      <CheckCircleIcon className="w-4 h-4" />
                    ) : (
                      <ExclamationCircleIcon className="w-4 h-4" />
                    )}
                    {passwordStrong ? 'Strong password' : 'At least 8 characters required'}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <LockClosedIcon className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="input-dark pl-11"
                    placeholder="Repeat password"
                  />
                </div>
                {formData.confirmPassword && (
                  <div className={`flex items-center gap-1 mt-1 text-xs ${passwordsMatch ? 'text-green-400' : 'text-red-400'}`}>
                    {passwordsMatch ? (
                      <CheckCircleIcon className="w-4 h-4" />
                    ) : (
                      <ExclamationCircleIcon className="w-4 h-4" />
                    )}
                    {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                  </div>
                )}
              </div>

              {/* Affiliate Code Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Affiliate Code (optional)
                </label>
                <div className="relative">
                  <GiftIcon className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={formData.affiliateCode}
                    onChange={(e) => setFormData({ ...formData, affiliateCode: e.target.value.toUpperCase() })}
                    className="input-dark pl-11"
                    placeholder="Enter code if you have one"
                    maxLength={10}
                  />
                </div>
                {formData.affiliateCode && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-primary-400">
                    <CheckCircleIcon className="w-4 h-4" />
                    Joining via affiliate code: {formData.affiliateCode}
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-1 rounded border-gray-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
                />
                <label htmlFor="terms" className="text-sm text-gray-400">
                  I accept the{' '}
                  <Link to="/terms" className="text-primary-400 hover:text-primary-300">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-primary-400 hover:text-primary-300">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              <button
                type="submit"
                disabled={isPending || !acceptTerms || !passwordsMatch || !passwordStrong}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="text-primary-400 hover:text-primary-300 font-medium">
                  Sign in now
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
